import { useCallback, useMemo, useState } from 'react';
import { PushToggle } from '../components/PushToggle';
import type { ShellCtx } from '../components/Shell';
import {
  type AgentAuditEntry,
  type AgentNotifyBody,
  type AgentStatusResponse,
  api,
} from '../lib/api';
import { useLoader } from '../lib/useLoader';

type Severity = 'low' | 'medium' | 'high' | 'critical';

function meterColor(pct: number): string {
  if (pct < 0.7) return '#22c55e';
  if (pct < 0.9) return '#f59e0b';
  return '#dc2626';
}

function Section({
  title,
  children,
  right,
}: { title: string; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <section
      style={{
        background: 'var(--bg-2)',
        border: '1px solid var(--border-1)',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: 14,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: 'var(--fg-2)',
          }}
        >
          {title}
        </h3>
        {right}
      </header>
      {children}
    </section>
  );
}

function Meter({ label, used, budget }: { label: string; used: number; budget: number }) {
  const pct = budget > 0 ? Math.min(used / budget, 1.5) : 0;
  const color = meterColor(pct);
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}
      >
        <span style={{ color: 'var(--fg-2)' }}>{label}</span>
        <span className="mono">
          ${used.toFixed(4)} / ${budget.toFixed(2)} ({(pct * 100).toFixed(0)}%)
        </span>
      </div>
      <div style={{ height: 8, background: 'var(--bg-3)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.min(pct * 100, 100)}%`, background: color }} />
      </div>
    </div>
  );
}

export function Notifications({ ctx }: { ctx: ShellCtx }) {
  const [status, setStatus] = useState<AgentStatusResponse | null>(null);
  const [audit, setAudit] = useState<AgentAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([api.agentStatus(), api.agentAudit({ limit: 100 })]);
      setStatus(s);
      setAudit(a.entries);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useLoader(refresh, ctx.refreshTick);

  const toggle = async (
    key: keyof Pick<
      NonNullable<typeof status>['settings'],
      'agentEnabled' | 'agentDailyReportEnabled' | 'agentAlertFormatEnabled'
    >,
  ) => {
    if (!status) return;
    setBusy(true);
    try {
      await api.agentPatchSettings({ [key]: !status.settings[key] } as never);
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const resetBreaker = async () => {
    setBusy(true);
    try {
      await api.agentResetBreaker();
      setFlash('Circuit breaker reset. Agent re-enabled.');
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const runReport = async (dryRun: boolean) => {
    setBusy(true);
    setFlash(null);
    try {
      const r = await api.agentDailyReportRun(dryRun);
      setFlash(
        r.ok
          ? dryRun
            ? `Dry run OK · cost $${(r.costUsd ?? 0).toFixed(4)}`
            : `Report sent · cost $${(r.costUsd ?? 0).toFixed(4)}`
          : `Report failed: ${r.error ?? 'unknown'}`,
      );
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const registerCommands = async () => {
    setBusy(true);
    try {
      const r = await api.agentRegisterCommands();
      setFlash(r.ok ? `Registered ${r.count} commands (${r.scope}).` : `Failed: ${r.error}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page" style={{ padding: '16px 24px' }}>
      <h1 style={{ marginBottom: 16 }}>Notifications</h1>

      {error && (
        <div
          style={{
            background: '#dc262633',
            border: '1px solid #dc2626',
            padding: 12,
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}
      {flash && (
        <div
          style={{
            background: '#22c55e33',
            border: '1px solid #22c55e',
            padding: 12,
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          {flash}
        </div>
      )}

      <Section title="Notificaciones push">
        <PushToggle />
      </Section>

      {loading && !status ? (
        <p>Loading…</p>
      ) : status ? (
        <>
          <Section title="Bot status">
            <BotStatus
              status={status}
              onToggle={toggle}
              onResetBreaker={resetBreaker}
              busy={busy}
              onRefresh={refresh}
            />
            <div style={{ marginTop: 12 }}>
              <button type="button" onClick={registerCommands} disabled={busy} className="btn">
                Register Discord commands
              </button>
            </div>
          </Section>

          <Section title="Cost control">
            <Meter label="Today" used={status.budget.today} budget={status.budget.dailyBudget} />
            <Meter label="Month" used={status.budget.month} budget={status.budget.monthlyBudget} />
            <BudgetEditor status={status} onSaved={refresh} setError={setError} />
          </Section>

          <Section
            title="Send custom notification"
            right={
              <span style={{ fontSize: 11, color: 'var(--fg-2)' }}>
                Posts to Discord webhook and/or email recipients.
              </span>
            }
          >
            <NotifyForm
              onSent={(msg) => {
                setFlash(msg);
                void refresh();
              }}
              setError={setError}
            />
          </Section>

          <Section
            title="Daily report"
            right={
              <span style={{ fontSize: 11, color: 'var(--fg-2)' }}>
                Hour UTC: {status.settings.agentDailyReportHourUtc} · last run:{' '}
                {status.settings.agentDailyReportLastRunDate ?? 'never'}
              </span>
            }
          >
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => runReport(true)} disabled={busy} className="btn">
                Send daily report (dry run)
              </button>
              <button
                type="button"
                onClick={() => runReport(false)}
                disabled={busy}
                className="btn btn-primary"
              >
                Send daily report (real)
              </button>
            </div>
          </Section>

          <Section title={`Agent audit log · ${audit.length} entries`}>
            <AuditTable rows={audit} />
          </Section>
        </>
      ) : null}
    </div>
  );
}

function BotStatus({
  status,
  onToggle,
  onResetBreaker,
  busy,
  onRefresh,
}: {
  status: AgentStatusResponse;
  onToggle: (k: 'agentEnabled' | 'agentDailyReportEnabled' | 'agentAlertFormatEnabled') => void;
  onResetBreaker: () => void;
  busy: boolean;
  onRefresh: () => void;
}) {
  const env = status.env;
  const tripped = status.breaker.trippedAt != null;
  return (
    <div>
      {tripped && (
        <div
          style={{
            background: '#dc262633',
            border: '1px solid #dc2626',
            padding: 12,
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          <strong>Circuit breaker tripped</strong> at {status.breaker.trippedAt}. Agent
          auto-disabled.
          <button
            type="button"
            onClick={onResetBreaker}
            disabled={busy}
            className="btn"
            style={{ marginLeft: 12 }}
          >
            Re-enable
          </button>
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 8,
        }}
      >
        <Indicator label="Anthropic key" ok={env.anthropicConfigured} />
        <Indicator label="Discord public key" ok={env.discordPublicKeyConfigured} />
        <Indicator label="Discord app ID" ok={env.discordApplicationIdConfigured} />
        <Indicator label="Discord bot token" ok={env.discordBotConfigured} />
        <Indicator label="Alert webhook" ok={env.discordWebhookConfigured} />
        <Indicator
          label="Guild ID"
          ok={Boolean(env.discordGuildId)}
          extra={env.discordGuildId ?? 'global'}
        />
      </div>
      <div
        style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}
      >
        <Toggle
          label="Agent ON"
          checked={status.settings.agentEnabled}
          onChange={() => onToggle('agentEnabled')}
          disabled={busy}
        />
        <Toggle
          label="Daily report"
          checked={status.settings.agentDailyReportEnabled}
          onChange={() => onToggle('agentDailyReportEnabled')}
          disabled={busy}
        />
        <Toggle
          label="Format alerts with LLM"
          checked={status.settings.agentAlertFormatEnabled}
          onChange={() => onToggle('agentAlertFormatEnabled')}
          disabled={busy}
        />
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--fg-2)' }}>
          {status.breaker.recentCalls} calls in last 5min · max {status.breaker.maxPer5min}
        </span>
        <button type="button" onClick={onRefresh} disabled={busy} className="btn">
          Refresh
        </button>
      </div>
    </div>
  );
}

function Indicator({ label, ok, extra }: { label: string; ok: boolean; extra?: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        background: 'var(--bg-3)',
        borderRadius: 8,
        border: '1px solid var(--border-1)',
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: ok ? '#22c55e' : '#6b7280',
        }}
      />
      <span style={{ fontSize: 12 }}>{label}</span>
      {extra && (
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--fg-2)' }}>{extra}</span>
      )}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
  disabled,
}: { label: string; checked: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <label
      style={{
        display: 'inline-flex',
        gap: 8,
        alignItems: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
      <span>{label}</span>
    </label>
  );
}

function BudgetEditor({
  status,
  onSaved,
  setError,
}: { status: AgentStatusResponse; onSaved: () => void; setError: (s: string) => void }) {
  const [daily, setDaily] = useState(String(status.budget.dailyBudget));
  const [monthly, setMonthly] = useState(String(status.budget.monthlyBudget));
  const [maxCalls, setMaxCalls] = useState(String(status.settings.agentMaxCallsPer5min));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.agentPatchSettings({
        agentDailyBudgetUsd: Number(daily),
        agentMonthlyBudgetUsd: Number(monthly),
        agentMaxCallsPer5min: Number(maxCalls),
      });
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 8,
        marginTop: 12,
      }}
    >
      <label style={{ fontSize: 12 }}>
        Daily budget USD
        <input
          type="number"
          step="0.01"
          value={daily}
          onChange={(e) => setDaily(e.target.value)}
          className="input"
          style={{ width: '100%' }}
        />
      </label>
      <label style={{ fontSize: 12 }}>
        Monthly budget USD
        <input
          type="number"
          step="0.5"
          value={monthly}
          onChange={(e) => setMonthly(e.target.value)}
          className="input"
          style={{ width: '100%' }}
        />
      </label>
      <label style={{ fontSize: 12 }}>
        Max LLM calls per 5min
        <input
          type="number"
          value={maxCalls}
          onChange={(e) => setMaxCalls(e.target.value)}
          className="input"
          style={{ width: '100%' }}
        />
      </label>
      <div style={{ display: 'flex', alignItems: 'flex-end' }}>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="btn btn-primary"
          style={{ width: '100%' }}
        >
          Save budgets
        </button>
      </div>
    </div>
  );
}

function NotifyForm({
  onSent,
  setError,
}: { onSent: (msg: string) => void; setError: (s: string) => void }) {
  const [severity, setSeverity] = useState<Severity>('medium');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [formatWithLlm, setFormatWithLlm] = useState(false);
  const [toDiscord, setToDiscord] = useState(true);
  const [emails, setEmails] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      const body: AgentNotifyBody = {
        severity,
        title,
        message,
        formatWithLlm,
        channels: {
          discord: toDiscord,
          email: emails
            .split(/[,;\s]+/)
            .map((s) => s.trim())
            .filter(Boolean),
        },
      };
      const r = await api.agentNotify(body);
      const summary = r.results.map((x) => `${x.channel}:${x.status}`).join(', ');
      onSent(`Sent: ${summary}`);
      setTitle('');
      setMessage('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8 }}>
        <select
          value={severity}
          onChange={(e) => setSeverity(e.target.value as Severity)}
          className="input"
        >
          <option value="low">low</option>
          <option value="medium">medium</option>
          <option value="high">high</option>
          <option value="critical">critical</option>
        </select>
        <input
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="input"
        />
      </div>
      <textarea
        placeholder="Message"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={4}
        className="input"
      />
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={toDiscord}
            onChange={(e) => setToDiscord(e.target.checked)}
          />
          Discord
        </label>
        <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={formatWithLlm}
            onChange={(e) => setFormatWithLlm(e.target.checked)}
          />
          Format with LLM
        </label>
        <input
          placeholder="Email recipients (comma separated)"
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          className="input"
          style={{ flex: 1, minWidth: 220 }}
        />
        <button
          type="button"
          onClick={send}
          disabled={sending || !title || !message || (!toDiscord && emails.trim() === '')}
          className="btn btn-primary"
        >
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
}

function AuditTable({ rows }: { rows: AgentAuditEntry[] }) {
  const fmt = useMemo(
    () =>
      new Intl.DateTimeFormat('es-ES', { hour12: false, dateStyle: 'short', timeStyle: 'medium' }),
    [],
  );
  if (rows.length === 0) return <p style={{ color: 'var(--fg-2)' }}>No audit entries yet.</p>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--fg-2)' }}>
            <th style={{ padding: '6px 8px' }}>When</th>
            <th style={{ padding: '6px 8px' }}>Trigger</th>
            <th style={{ padding: '6px 8px' }}>Command / Tool</th>
            <th style={{ padding: '6px 8px' }}>User</th>
            <th style={{ padding: '6px 8px' }}>Status</th>
            <th style={{ padding: '6px 8px' }}>Cost</th>
            <th style={{ padding: '6px 8px' }}>ms</th>
            <th style={{ padding: '6px 8px' }}>Error</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={{ borderTop: '1px solid var(--border-1)' }}>
              <td className="mono" style={{ padding: '6px 8px' }}>
                {fmt.format(new Date(r.timestamp))}
              </td>
              <td style={{ padding: '6px 8px' }}>{r.trigger}</td>
              <td style={{ padding: '6px 8px' }}>{r.command ?? r.toolName ?? ''}</td>
              <td className="mono" style={{ padding: '6px 8px' }}>
                {r.discordUserId ?? '—'}
              </td>
              <td
                style={{
                  padding: '6px 8px',
                  color:
                    r.status === 'ok' ? '#22c55e' : r.status === 'denied' ? '#f97316' : '#dc2626',
                  fontWeight: 600,
                }}
              >
                {r.status}
              </td>
              <td className="mono" style={{ padding: '6px 8px' }}>
                {r.costUsd ? `$${Number(r.costUsd).toFixed(4)}` : '—'}
              </td>
              <td className="mono" style={{ padding: '6px 8px' }}>
                {r.latencyMs ?? '—'}
              </td>
              <td style={{ padding: '6px 8px', color: 'var(--fg-2)' }} title={r.errorMessage ?? ''}>
                {r.errorMessage ? r.errorMessage.slice(0, 60) : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
